import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import CelebrationHero from './components/CelebrationHero';
import NextStepsSection from './components/NextStepsSection';
import SocialEngagement from './components/SocialEngagement';
import ContentRecommendations from './components/ContentRecommendations';

const ThankYouPage = () => {
  const [userName, setUserName] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);

    // Get user data from localStorage or URL params
    const urlParams = new URLSearchParams(window.location.search);
    const nameFromUrl = urlParams?.get('name');
    const refFromUrl = urlParams?.get('ref');
    
    const storedName = localStorage.getItem('enquiryUserName');
    const storedRef = localStorage.getItem('enquiryReference');

    setUserName(nameFromUrl || storedName || '');
    setReferenceNumber(refFromUrl || storedRef || generateReferenceNumber());

    // Clear stored data after use
    if (storedName) localStorage.removeItem('enquiryUserName');
    if (storedRef) localStorage.removeItem('enquiryReference');
  }, []);

  const generateReferenceNumber = () => {
    const timestamp = Date.now()?.toString()?.slice(-6);
    const random = Math.random()?.toString(36)?.substring(2, 5)?.toUpperCase();
    return `MAVA${timestamp}${random}`;
  };

  return (
    <>
      <Helmet>
        <title>Thank You - Mava Cold Beverages | Your Enquiry Received</title>
        <meta 
          name="description" 
          content="Thank you for your enquiry! Our hydration experts will contact you within 24-48 hours with personalized recommendations. Stay Cool. Stay Mava." 
        />
        <meta name="keywords" content="thank you, enquiry received, mava cold beverages, hydration experts, premium bottles" />
        <meta name="robots" content="noindex, nofollow" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Thank You - Mava Cold Beverages" />
        <meta property="og:description" content="Your enquiry has been successfully submitted. Our team will respond within 24-48 hours." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mava.com/thank-you-page" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Thank You - Mava Cold Beverages" />
        <meta name="twitter:description" content="Your enquiry has been successfully submitted. Our team will respond within 24-48 hours." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Thank You Page",
            "description": "Enquiry confirmation page for Mava Cold Beverages",
            "url": "https://mava.com/thank-you-page",
            "isPartOf": {
              "@type": "WebSite",
              "name": "Mava Cold Beverages",
              "url": "https://mava.com"
            }
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          {/* Hero Section with Celebration Animation */}
          <CelebrationHero 
            userName={userName}
            referenceNumber={referenceNumber}
          />

          {/* Next Steps Information */}
          <NextStepsSection />

          {/* Social Media & Newsletter Engagement */}
          <SocialEngagement />

          {/* Content Recommendations & Downloads */}
          <ContentRecommendations />
        </main>

        {/* Footer */}
        <footer className="bg-secondary text-secondary-foreground py-8 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current">
                  <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
                </svg>
              </div>
              <span className="text-xl font-poppins font-bold">Mava</span>
            </div>
            
            <p className="text-sm opacity-80 mb-2">
              Premium Cold Beverages • Sustainable Hydration • Stay Cool. Stay Mava.
            </p>
            
            <p className="text-xs opacity-60">
              © {new Date()?.getFullYear()} Mava Cold Beverages. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ThankYouPage;